import React, { useContext, useState } from 'react';
import { RegularButton as Button, LinkButton, Typography } from 'fronton-react';
import { CheckIcon, InfoIcon } from '@fronton/icons-react';
import { getCookie, setCookie } from 'utils/cookie.helper';
import { getDomain } from 'utils/get-domain.helper';
import { getRegion } from 'utils/get-region-code.helper';
import { getHostname } from 'utils/get-hostname.helper';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import type { TBasketItem } from 'pages/calculation/types/basket-item.type';
import { getUpdatedBasket } from 'pages/calculation/helpers/get-updated-basket.helper';
import { deserealizeBasket } from 'pages/calculation/helpers/deserealize-basket.helper';
import { serealizeBasket } from 'pages/calculation/helpers/serealize-basket.helper';
import { setBasket } from 'pages/calculation/api/set-basket.service';
import { useCalculationState } from 'pages/calculation/store/calculation';
import { DataContext } from 'entries/common/widgets/context';
import { gaAddToCart } from 'ga-events/ga-add-to-cart';
import { gaClickOnGoods } from 'ga-events/ga-click-goods';
import { dimensionsAtom } from 'pages/dimensions/store/dimensions';
import { useRecoilValue } from 'recoil';
import { getComplimentaryProductInfoForBasket } from 'pages/calculation/helpers/get-complimentary-product-info-for-basket.helper';
import { addComplimentaryProductsToBasketArr } from 'pages/calculation/helpers/add-complimentary-products-to-basket-arr.helper';
import { getAllProductsInBasket } from 'pages/calculation/helpers/get-all-products-in-basket.helper';
import { getProductQuantityInBasket } from '../../../helpers/get-product-quantity-in-basket.helper';
import { ContainerButtons, goToCartButtonClass, NoticeContainer } from './cart.styles';

const COOCKIE_KEY = 'cartInfo';
const BASKET_URL = 'basket';

type TCartComponentProps = {
  productId: string;
  count: number;
};

export const CartComponent: React.FC<TCartComponentProps> = ({ productId, count }) => {
  const data = useContext(DataContext);
  const { mobile, tablet } = useBreakpoints() ?? {};
  const { calculationFormState, setCalculationState } = useCalculationState();
  const { addToCartIsSuccessful } = calculationFormState;
  const basketItems = deserealizeBasket(getCookie(COOCKIE_KEY) || '');

  const productQuantityInBasket = getProductQuantityInBasket(basketItems, productId);
  const dimensions = useRecoilValue(dimensionsAtom);
  const complimentaryProductInfoForBasket = getComplimentaryProductInfoForBasket(dimensions);
  const basketArr: TBasketItem[] = getUpdatedBasket(basketItems, {
    code: productId,
    quantity: productQuantityInBasket + count,
  });

  const complementaryProductsWithBasketProducts = addComplimentaryProductsToBasketArr({
    basketArr,
    complimentaryProductInfoForBasket,
  });
  const productCodesInBasket = complementaryProductsWithBasketProducts.map(item => item.code);
  const isAddedProductsInBasket = basketItems.some(item => productCodesInBasket.includes(item.code));

  const [showTooltip, setShowTooltip] = useState(isAddedProductsInBasket);

  const handleClick = async () => {
    setShowTooltip(false);

    const allProductsInBasket = getAllProductsInBasket({ dimensions, data, count });
    allProductsInBasket.forEach(item =>
      gaAddToCart({
        id: item.productId,
        quantity: item.productQuantity,
        price: item.price ?? 0,
        name: item.name ?? '',
      })
    );

    const domain = getDomain();
    setCookie(COOCKIE_KEY, serealizeBasket(complementaryProductsWithBasketProducts), {
      path: '/',
      domain,
      expires: new Date(new Date().getFullYear() + 1, 12, 31),
    });
    document.body.dispatchEvent(
      new CustomEvent('basket:update', {
        cancelable: true,
        bubbles: true,
        composed: true,
        detail: complementaryProductsWithBasketProducts,
      })
    );
    const isAuthorized = getCookie('user-auth') === 'true';
    const regionCode = getRegion(getHostname(), domain);

    try {
      if (isAuthorized) {
        const apiMethods = data!.api;
        await setBasket({
          products: complementaryProductsWithBasketProducts.map(({ code, quantity }) => ({
            id: code,
            quantity,
          })),
          regionCode: regionCode || undefined,
          api: apiMethods,
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
    setCalculationState(prevState => ({
      ...prevState,
      addToCartIsSuccessful: true,
    }));
  };

  if (addToCartIsSuccessful) {
    return (
      <ContainerButtons>
        <Button
          data-testid="calculation_in_cart_button"
          size={mobile || tablet ? 'm' : 'l'}
          variant="outline"
          iconLeft={<CheckIcon type="outline" size="l" color="text-primary" />}
          onClick={() => {
            gaClickOnGoods({
              title: 'Добавлено в корзину',
              name: 'В корзине',
            });
          }}>
          В корзине
        </Button>
        <LinkButton
          data-testid="calculation_go_to_cart_button"
          className={goToCartButtonClass}
          href={`${window?.location?.origin}/${BASKET_URL}/`}
          target="_blank"
          size={mobile || tablet ? 's' : 'm'}
          variant="accent"
          onClick={() => {
            gaClickOnGoods({
              title: 'Добавлено в корзину',
              name: 'Перейти в корзину',
            });
          }}
          withExtraClickZone>
          Перейти в корзину
        </LinkButton>
      </ContainerButtons>
    );
  }

  return (
    <>
      <Button
        size={mobile ? 's' : 'l'}
        variant="primary"
        label="В корзину"
        type="submit"
        form="calculation-form"
        onClick={handleClick}>
        В корзину
      </Button>
      {showTooltip ? (
        <NoticeContainer>
          <InfoIcon type="outline" size="s" color="text-primary" strokeWidth={1.5} />
          <Typography variant="caption" color="text-primary">
            Некоторые товары из расчёта уже добавлены в корзину
          </Typography>
        </NoticeContainer>
      ) : null}
    </>
  );
};
